import { useCallback } from 'react';
import {
  useMapLegendColors,
  useSaveMapLegendsToSettings,
} from '@ais-ui/websockets';

import { useSettings } from '../../../Settings/SettingsProvider';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useMapOverlay } from '../../contexts/MapOverlayContext';
import { useReplayContext } from '../../../Replay/ReplayContext'; // Replace with your actual context
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

export const useMapLegendSettings = () => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { data: legendColors } = useMapLegendColors();
  const AISMapContext = useMapOverlay();
  const { isPlaying } = useReplayContext();
  const { mutate } = useSaveMapLegendsToSettings(isPlaying);
  const { toast } = useToast();

  const getSettingsWithCategoriesSetToNoShow = useCallback(
    (categories: string[]) => {
      return {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          ship_color_noshow_sel: categories,
          ship_color_noshow_sel_id:
            settings['ribbonPlugin.settings']?.legend_shiplists,
        },
      };
    },
    [settings],
  );

  const getSettingsWithCategoriesSetToShow = useCallback(() => {
    return {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        ship_color_noshow_sel: [],
        ship_color_noshow_sel_id: [],
      },
    };
  }, [settings]);

  const getUpdatedLegendSettings = useCallback(
    (isActive: boolean, category: string, id?: string) => {
      const newShipColorNoShowSel = [
        ...(settings['ribbonPlugin.settings']['ship_color_noshow_sel'] || []),
      ];
      const newShipColorNoShowSelId = [
        ...(settings['ribbonPlugin.settings']['ship_color_noshow_sel_id'] ||
          []),
      ];

      if (!isActive) {
        // Remove the item if it exists
        const index = newShipColorNoShowSel.indexOf(category);
        const indexId = newShipColorNoShowSelId.indexOf(id);
        if (index > -1) {
          newShipColorNoShowSel.splice(index, 1);
        }
        if (indexId > -1) {
          newShipColorNoShowSelId.splice(indexId, 1);
        }
      } else {
        // Add the item if it doesn't exist
        if (!newShipColorNoShowSel.includes(category)) {
          newShipColorNoShowSel.push(category);
        }

        if (
          (!newShipColorNoShowSelId.includes(id) && id !== undefined) ||
          null
        ) {
          newShipColorNoShowSelId.push(id);
        }
      }

      // Create the new settings object
      return {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          ship_color_noshow_sel: newShipColorNoShowSel,
          ship_color_noshow_sel_id: newShipColorNoShowSelId,
        },
      };
    },
    [settings],
  );

  const onDisableAll = () => {
    const categories = legendColors
      ? legendColors.map((color) => color.name)
      : [];

    const settingsToSave = getSettingsWithCategoriesSetToNoShow(categories);

    mutate(
      {
        settings: settingsToSave,
        mapLegendItemsArgs: { mapLegendItems: categories, showItem: false },
      },
      {
        onSuccess: () => {
          toast({
            className: 'toggle-legend-item-success-toast',
            title: 'Success!',
            description: 'All categories have been disabled',
          });
          AISMapContext.setTrigger((prevTrigger) => prevTrigger + 1);
        },
        onError: (error) => {
          markErrorHandledLocally(error);
          toast({
            className: 'set-legend-display-settings-error-toast',
            title: 'Error!',
            description: messages.mapMenuLegendFailedApply,
            variant: 'destructive',
          });
        },
      },
    );
  };

  const onEnableAll = () => {
    const categories = legendColors
      ? legendColors.map((color) => color.name)
      : [];
    const settingsToSave = getSettingsWithCategoriesSetToShow();

    mutate(
      {
        settings: settingsToSave,
        mapLegendItemsArgs: { mapLegendItems: categories, showItem: true },
      },
      {
        onSuccess: () => {
          toast({
            className: 'toggle-legend-item-success-toast',
            title: 'Success!',
            description: 'All categories have been enabled',
          });
          AISMapContext.setTrigger((prevTrigger) => prevTrigger + 1);
        },
        onError: (error) => {
          markErrorHandledLocally(error);
          toast({
            className: 'set-legend-display-settings-error-toast',
            title: 'Error!',
            description: messages.mapMenuLegendFailedApply,
            variant: 'destructive',
          });
        },
      },
    );
  };

  const onActivate = (isActive: boolean, category: string, id?: string) => {
    const categories = [category];
    const settingsToSave = getUpdatedLegendSettings(isActive, category, id);
    mutate(
      {
        settings: settingsToSave,
        mapLegendItemsArgs: { mapLegendItems: categories, showItem: !isActive },
      },
      {
        onSuccess: () => {
          toast({
            className: 'toggle-legend-item-success-toast',
            title: 'Success!',
            description: messages.mapMenuLegendToggledSuccesfully.replace(
              '{label}',
              category,
            ),
          });
          AISMapContext.setTrigger((prevTrigger) => prevTrigger + 1);
        },
        onError: (error) => {
          markErrorHandledLocally(error);
          toast({
            className: 'set-legend-display-settings-error-toast',
            title: 'Error!',
            description: messages.mapMenuLegendFailedApply,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return {
    onActivate,
    onDisableAll,
    onEnableAll,
  };
};
